import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Card, Button, EmptyState, Modal } from '../components/ui';

const fmtTRY = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(Number(n) || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';

/* ── Limit kullanım çubuğu ───────────────────────────────────────── */
function LimitBar({ label, used, limit, unlimited, suffix = '' }) {
  if (unlimited) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', marginBottom: 4 }}>
          <span style={{ color:'var(--text-secondary)' }}>{label}</span>
          <span style={{ color:'var(--success)', fontWeight:600 }}>Sınırsız</span>
        </div>
        <div style={{ height: 4, background:'var(--bg-tertiary)', borderRadius: 2 }} />
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const color = pct >= 90 ? 'var(--danger)' : pct >= 75 ? 'var(--warning, #f59e0b)' : 'var(--accent-primary)';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', marginBottom: 4 }}>
        <span style={{ color:'var(--text-secondary)' }}>{label}</span>
        <span style={{ color: pct >= 75 ? color : 'var(--text-primary)', fontWeight:600, fontVariantNumeric:'tabular-nums' }}>
          {used.toLocaleString('tr-TR')}{suffix} / {limit.toLocaleString('tr-TR')}{suffix}
        </span>
      </div>
      <div style={{ height: 4, background:'var(--bg-tertiary)', borderRadius: 2, overflow:'hidden' }}>
        <div style={{ height:'100%', width: pct + '%', background: color, transition:'width 0.4s' }} />
      </div>
    </div>
  );
}

/* ── Plan badge ───────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    trial:         { label: 'Deneme', color: '#7c3aed', bg: 'rgba(124,58,237,0.12)' },
    trial_expired: { label: 'Deneme Bitti', color: '#dc2626', bg: 'rgba(239,68,68,0.12)' },
    active:        { label: 'Aktif', color: '#059669', bg: 'rgba(16,185,129,0.12)' },
    past_due:      { label: 'Vade Geçti', color: '#d97706', bg: 'rgba(245,158,11,0.12)' },
    suspended:     { label: 'Askıda', color: '#dc2626', bg: 'rgba(239,68,68,0.12)' },
    cancelled:     { label: 'İptal', color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
  };
  const m = map[status] || { label: status || 'Bilinmiyor', color: '#64748b', bg: 'rgba(100,116,139,0.12)' };
  return (
    <span style={{ padding:'3px 10px', borderRadius:100, fontSize:'0.72rem', fontWeight:700,
                   color: m.color, background: m.bg, letterSpacing:'0.04em', textTransform:'uppercase' }}>
      {m.label}
    </span>
  );
}

/* ── Ana sayfa ───────────────────────────────────────────────────── */
export default function SubscriptionPage() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState(null);
  const [plans, setPlans]     = useState([]);
  const [addons, setAddons]   = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [vendorMethods, setVendorMethods] = useState([]);
  const [busy, setBusy]       = useState(null);
  const [msg, setMsg]         = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponInput, setCouponInput] = useState('EARLYBIRD');
  const [couponResult, setCouponResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, a, i, pm, vpm] = await Promise.all([
        api.get('/subscription/status'),
        api.get('/subscription/plans'),
        api.get('/subscription/addons'),
        api.get('/subscription/invoices'),
        api.get('/subscription/payment-methods'),
        api.get('/subscription/vendor-payment-methods'),
      ]);
      setStatus(s.data.data);
      setPlans(p.data.data || []);
      setAddons(a.data.data || []);
      setInvoices(i.data.data || []);
      setPaymentMethods(pm.data.data || []);
      setVendorMethods(vpm.data.data || []);
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Veriler alınamadı' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const subscribeAddon = async (addonId) => {
    setBusy('addon-' + addonId);
    try {
      await api.post(`/subscription/addons/${addonId}/subscribe`, { billing_period: 'monthly' });
      setMsg({ type:'success', text:'Add-on aktif edildi' });
      await load();
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Hata' });
    }
    setBusy(null);
  };

  const cancelAddon = async (tenantAddonId) => {
    if (!window.confirm('Bu add-on iptal edilsin mi? Mevcut dönem sonunda devre dışı kalır.')) return;
    setBusy('cancel-' + tenantAddonId);
    try {
      await api.post(`/subscription/addons/${tenantAddonId}/cancel`, { reason: 'user_cancelled' });
      setMsg({ type:'success', text:'Add-on iptal edildi' });
      await load();
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Hata' });
    }
    setBusy(null);
  };

  const validateCoupon = async () => {
    setCouponResult(null);
    try {
      const r = await api.post('/subscription/coupons/validate', { code: couponInput });
      setCouponResult({ ok: true, data: r.data.data });
    } catch (err) {
      setCouponResult({ ok: false, msg: err.response?.data?.message || 'Hata' });
    }
  };

  const removePaymentMethod = async (id) => {
    if (!window.confirm('Bu kart silinsin mi?')) return;
    setBusy('pm-' + id);
    try {
      await api.delete(`/subscription/payment-methods/${id}`);
      await load();
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Hata' });
    }
    setBusy(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign:'center', color:'var(--text-tertiary)' }}>Yükleniyor...</div>;

  const company = status?.company;
  const limits  = status?.limits || {};
  const activeAddons = status?.addons || [];
  const activeCoupons = status?.coupons || [];
  const provider = status?.provider || 'iyzico';

  const currentPlan = plans.find(p => p.id === company?.plan_id);
  const isProvider = (n) => provider === n;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 4px' }}>
      {/* Üst başlık */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom: 16, flexWrap:'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'var(--text-primary)', margin:'0 0 4px', letterSpacing:'-0.02em' }}>
            Aboneliğim
          </h1>
          <p style={{ color:'var(--text-secondary)', margin:0, fontSize:'0.85rem' }}>
            Plan, kullanım, add-on'lar, faturalar ve ödeme bilgileri
          </p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>Ödeme sağlayıcı:</span>
          <span style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--text-primary)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            {provider}
          </span>
        </div>
      </div>

      {msg && (
        <div style={{ marginBottom: 14, padding:'10px 14px', borderRadius:8,
                      background: msg.type === 'error' ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)',
                      border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.30)' : 'rgba(16,185,129,0.30)'),
                      color: msg.type === 'error' ? '#b91c1c' : '#059669',
                      fontSize:'0.85rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:'1rem' }}>✕</button>
        </div>
      )}

      {/* 1. Mevcut Plan + Kullanım */}
      <Card style={{ marginBottom: 16 }}>
        <div className="sub-plan-grid">
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: 6 }}>
              <StatusBadge status={company?.subscription_status} />
              {status?.days_left != null && (
                <span style={{ fontSize:'0.74rem', color:'var(--text-tertiary)' }}>
                  {status.days_left > 0 ? `${status.days_left} gün kaldı` : 'Süresi doldu'}
                </span>
              )}
            </div>
            <div style={{ fontSize:'1.4rem', fontWeight:700, color:'var(--text-primary)', letterSpacing:'-0.02em' }}>
              {company?.plan_name || 'Plan seçilmedi'}
            </div>
            <div style={{ fontSize:'0.78rem', color:'var(--text-tertiary)', marginTop: 2 }}>
              {company?.billing_period === 'yearly' ? 'Yıllık fatura' : 'Aylık fatura'} ·{' '}
              {fmtTRY(company?.billing_period === 'yearly' ? company?.yearly_price : company?.monthly_price)}/{company?.billing_period === 'yearly' ? 'yıl' : 'ay'}
            </div>
            {company?.early_bird_applied && (
              <div style={{ marginTop: 8, padding:'4px 10px', borderRadius:6, fontSize:'0.72rem', fontWeight:600,
                            background:'rgba(124,58,237,0.12)', color:'#7c3aed', display:'inline-block' }}>
                🌟 Erken Kuş indirimi aktif
              </div>
            )}
            <div style={{ marginTop: 16, display:'flex', gap: 6, flexWrap:'wrap' }}>
              <Button variant="primary" size="sm">Planı Yükselt</Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCouponModal(true)}>Kupon Kullan</Button>
            </div>
          </div>
          <div>
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.08em',
                          textTransform:'uppercase', marginBottom: 12 }}>
              Bu ayın kullanımı
            </div>
            <LimitBar label="Kullanıcı" used={limits.user?.used || 0} limit={limits.user?.limit || 0} unlimited={limits.user?.unlimited} />
            <LimitBar label="Cari kartı" used={limits.account?.used || 0} limit={limits.account?.limit || 0} unlimited={limits.account?.unlimited} />
            <LimitBar label="Stok kartı" used={limits.stock?.used || 0} limit={limits.stock?.limit || 0} unlimited={limits.stock?.unlimited} />
            <LimitBar label="Aylık fatura/irsaliye/sipariş" used={limits.monthly_invoice?.used || 0} limit={limits.monthly_invoice?.limit || 0} unlimited={limits.monthly_invoice?.unlimited} />
            <LimitBar label="Depolama" used={limits.storage?.used || 0} limit={limits.storage?.limit || 0} unlimited={limits.storage?.unlimited} suffix=" MB" />
          </div>
        </div>
      </Card>

      {/* 2. Aktif kuponlar */}
      {activeCoupons.length > 0 && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--text-secondary)', marginBottom: 10 }}>
            Uygulanan İndirimler
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap: 8 }}>
            {activeCoupons.map(c => (
              <div key={c.id} style={{ padding:'6px 12px', borderRadius:8,
                                       background:'rgba(124,58,237,0.10)', border:'1px solid rgba(124,58,237,0.25)',
                                       fontSize:'0.78rem', color:'#7c3aed' }}>
                <strong>{c.code}</strong> · {c.discount_type === 'percent' ? `%${c.discount_value} indirim` : fmtTRY(c.discount_value) + ' indirim'}
                {c.expires_at && <span style={{ marginLeft:6, opacity:0.7 }}>· {fmtDate(c.expires_at)}'e kadar</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 3. Aktif Add-on'lar */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border-secondary)',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text-primary)' }}>
              Eklentiler
            </div>
            <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
              Aktif: <b>{activeAddons.length}</b>
            </div>
          </div>
        </div>
        {activeAddons.length === 0 ? (
          <div style={{ padding: 24, textAlign:'center', color:'var(--text-tertiary)', fontSize:'0.85rem' }}>
            Henüz eklenti yok — aşağıdaki mağazadan ekleyebilirsin
          </div>
        ) : (
          <div>
            {activeAddons.map(a => (
              <div key={a.id} style={{ display:'flex', alignItems:'center', gap: 14, padding:'12px 18px',
                                       borderBottom:'1px solid var(--border-secondary)' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.88rem' }}>
                    {a.name}
                    <span style={{ marginLeft: 8, fontSize:'0.7rem', fontWeight:600, color:'#059669',
                                   background:'rgba(16,185,129,0.10)', padding:'1px 7px', borderRadius:100 }}>Aktif</span>
                  </div>
                  <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
                    {a.description}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginTop: 4 }}>
                    {a.billing_period === 'yearly' ? 'Yıllık' : 'Aylık'} · {fmtTRY(a.billing_period === 'yearly' ? a.yearly_price : a.monthly_price)} ·
                    Bitiş: {fmtDate(a.ends_at)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => cancelAddon(a.id)} disabled={busy === 'cancel-' + a.id}
                  style={{ color:'var(--danger)' }}>İptal</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 4. Add-on mağaza */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border-secondary)' }}>
          <div style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text-primary)' }}>
            Eklenti Mağazası
          </div>
          <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
            İhtiyacın olan modülleri tek tıkla ekle, istediğin zaman iptal et
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: 14 }}>
          {addons.filter(a => !activeAddons.find(t => t.addon_id === a.id)).map(a => (
            <div key={a.id} style={{ padding: 14, background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)',
                                     borderRadius: 8, display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.92rem' }}>
                {a.name}
              </div>
              <div style={{ fontSize:'0.76rem', color:'var(--text-tertiary)', lineHeight:1.5, flex:1, minHeight: 40 }}>
                {a.description}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:'auto' }}>
                <div>
                  <div style={{ fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)', fontVariantNumeric:'tabular-nums' }}>
                    {fmtTRY(a.monthly_price)}
                  </div>
                  <div style={{ fontSize:'0.7rem', color:'var(--text-tertiary)' }}>/ ay (KDV hariç)</div>
                </div>
                <Button variant="primary" size="sm" onClick={() => subscribeAddon(a.id)} disabled={busy === 'addon-' + a.id}>
                  {busy === 'addon-' + a.id ? '...' : '+ Ekle'}
                </Button>
              </div>
            </div>
          ))}
          {addons.filter(a => !activeAddons.find(t => t.addon_id === a.id)).length === 0 && (
            <div style={{ gridColumn:'1/-1', textAlign:'center', padding: 24, color:'var(--text-tertiary)', fontSize:'0.85rem' }}>
              Tüm eklentiler aktif 🎉
            </div>
          )}
        </div>
      </Card>

      {/* 5. Ödeme Yöntemleri */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border-secondary)',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text-primary)' }}>Ödeme Yöntemleri</div>
            <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
              Otomatik tekrarlama için kayıtlı kartlar
            </div>
          </div>
          <Button variant="primary" size="sm" disabled>+ Yeni Kart (yakında)</Button>
        </div>
        {paymentMethods.length === 0 ? (
          <div style={{ padding: 24, textAlign:'center', color:'var(--text-tertiary)', fontSize:'0.85rem' }}>
            Henüz kayıtlı kart yok
          </div>
        ) : (
          <div>
            {paymentMethods.map(pm => (
              <div key={pm.id} style={{ display:'flex', alignItems:'center', gap: 14, padding:'12px 18px',
                                        borderBottom:'1px solid var(--border-secondary)' }}>
                <div style={{ width: 40, height: 28, background:'linear-gradient(135deg, #0f172a, #334155)', borderRadius: 4,
                              display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff',
                              fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.06em' }}>
                  {(pm.card_brand || 'CARD').toUpperCase().slice(0, 4)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.88rem', fontFamily:'monospace' }}>
                    •••• •••• •••• {pm.card_last_4 || '????'}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>
                    {pm.card_holder || '—'} · {pm.expiry_month}/{pm.expiry_year} · {pm.provider}
                    {pm.is_default && <span style={{ marginLeft: 8, color:'#059669', fontWeight:600 }}>· Varsayılan</span>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removePaymentMethod(pm.id)} disabled={busy === 'pm-' + pm.id}
                  style={{ color:'var(--danger)' }}>Sil</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 5b. Vendor'un Yayınladığı Ödeme Kanalları (havale/IBAN vs.) */}
      {vendorMethods.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border-secondary)' }}>
            <div style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text-primary)' }}>Ödeme Kanalları</div>
            <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
              Aboneliğinizi aşağıdaki kanallar üzerinden ödeyebilirsiniz
            </div>
          </div>
          <div style={{ padding: 16, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {vendorMethods.map(m => (
              <div key={m.id} style={{ border:'1px solid var(--border-secondary)', borderRadius: 8, padding: 14, background:'var(--bg-secondary)' }}>
                <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>{m.type === 'bank_transfer' ? '🏦' : m.type === 'credit_card' ? '💳' : '💰'}</span>
                  <strong style={{ fontSize: 14 }}>{m.name}</strong>
                  <span style={{ marginLeft:'auto', fontSize: 11, padding:'2px 8px', borderRadius: 100, background:'rgba(100,116,139,0.10)', color:'var(--text-tertiary)', fontWeight: 600 }}>{m.currency}</span>
                </div>
                {m.iban && (
                  <div style={{ fontFamily:'monospace', fontSize: 13, padding:'8px 10px', background:'var(--bg-primary)', borderRadius: 6, marginBottom: 6, userSelect:'all', letterSpacing:'0.05em' }}>
                    {m.iban.replace(/(.{4})/g, '$1 ').trim()}
                  </div>
                )}
                {(m.bank_name || m.account_name) && (
                  <div style={{ fontSize: 12, color:'var(--text-secondary)' }}>
                    {m.bank_name}{m.account_name ? ' · ' + m.account_name : ''}
                  </div>
                )}
                {m.description && (
                  <div style={{ fontSize: 12, color:'var(--text-tertiary)', marginTop: 8, paddingTop: 8, borderTop:'1px dashed var(--border-secondary)' }}>
                    {m.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 6. Fatura Geçmişi */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border-secondary)' }}>
          <div style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text-primary)' }}>Fatura Geçmişi</div>
          <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>
            Tarafımıza kesilen abonelik faturaları
          </div>
        </div>
        {invoices.length === 0 ? (
          <div style={{ padding: 24, textAlign:'center', color:'var(--text-tertiary)', fontSize:'0.85rem' }}>
            Henüz fatura yok
          </div>
        ) : (
          <div className="sub-invoice-table-wrap">
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
            <thead>
              <tr style={{ background:'var(--bg-tertiary)', borderBottom:'1px solid var(--border-secondary)' }}>
                <th style={{ padding:'10px 18px', textAlign:'left', fontWeight:600, color:'var(--text-tertiary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>Fatura</th>
                <th style={{ padding:'10px 18px', textAlign:'left', fontWeight:600, color:'var(--text-tertiary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>Tarih</th>
                <th style={{ padding:'10px 18px', textAlign:'left', fontWeight:600, color:'var(--text-tertiary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>Dönem</th>
                <th style={{ padding:'10px 18px', textAlign:'right', fontWeight:600, color:'var(--text-tertiary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>Tutar</th>
                <th style={{ padding:'10px 18px', textAlign:'center', fontWeight:600, color:'var(--text-tertiary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>Durum</th>
                <th style={{ padding:'10px 18px', width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} style={{ borderBottom:'1px solid var(--border-secondary)' }}>
                  <td style={{ padding:'12px 18px', fontFamily:'monospace', color:'var(--text-primary)', fontWeight:600 }}>
                    {inv.invoice_number || inv.id.slice(0, 8)}
                  </td>
                  <td style={{ padding:'12px 18px', color:'var(--text-secondary)' }}>{fmtDate(inv.invoice_date)}</td>
                  <td style={{ padding:'12px 18px', color:'var(--text-secondary)', fontSize:'0.76rem' }}>
                    {inv.period_start ? `${fmtDate(inv.period_start)} - ${fmtDate(inv.period_end)}` : '—'}
                  </td>
                  <td style={{ padding:'12px 18px', textAlign:'right', fontWeight:600, fontVariantNumeric:'tabular-nums', color:'var(--text-primary)' }}>
                    {fmtTRY(inv.total_amount)}
                  </td>
                  <td style={{ padding:'12px 18px', textAlign:'center' }}>
                    {inv.status === 'paid' && <span style={{ color:'#059669', fontWeight:600, fontSize:'0.76rem' }}>● Ödendi</span>}
                    {inv.status === 'pending' && <span style={{ color:'#d97706', fontWeight:600, fontSize:'0.76rem' }}>● Bekliyor</span>}
                    {inv.status === 'overdue' && <span style={{ color:'#dc2626', fontWeight:600, fontSize:'0.76rem' }}>● Gecikti</span>}
                    {inv.status === 'cancelled' && <span style={{ color:'#64748b', fontWeight:600, fontSize:'0.76rem' }}>● İptal</span>}
                  </td>
                  <td style={{ padding:'8px 18px', textAlign:'right' }}>
                    {inv.status !== 'paid' && (
                      <Button variant="primary" size="sm">Öde</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      {/* Kupon modali */}
      {showCouponModal && (
        <Modal title="Kupon Kullan" onClose={() => { setShowCouponModal(false); setCouponResult(null); }}>
          <div style={{ padding: 18 }}>
            <label style={{ fontSize:'0.78rem', fontWeight:500, color:'var(--text-secondary)', display:'block', marginBottom: 6 }}>
              Kupon kodu
            </label>
            <input className="ui-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="örn. EARLYBIRD" autoFocus />
            <div style={{ marginTop: 12, display:'flex', gap:8 }}>
              <Button variant="primary" onClick={validateCoupon}>Kontrol Et</Button>
            </div>
            {couponResult && (
              <div style={{ marginTop: 14, padding: 12, borderRadius: 8,
                            background: couponResult.ok ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                            border: '1px solid ' + (couponResult.ok ? 'rgba(16,185,129,0.30)' : 'rgba(239,68,68,0.30)'),
                            fontSize:'0.84rem' }}>
                {couponResult.ok ? (
                  <>
                    <div style={{ fontWeight:600, color:'#059669', marginBottom: 4 }}>✓ {couponResult.data.name}</div>
                    <div style={{ color:'var(--text-secondary)' }}>
                      {couponResult.data.discount_type === 'percent'
                        ? `%${couponResult.data.discount_value} indirim`
                        : `${fmtTRY(couponResult.data.discount_value)} indirim`} ·{' '}
                      {couponResult.data.duration_months} ay süreli
                    </div>
                    <Button variant="primary" size="sm" style={{ marginTop: 10 }}
                      onClick={() => { setMsg({ type:'success', text:'Kupon uygulandı. Plan veya add-on satın alırken etkin olacak.' });
                                       setShowCouponModal(false); setCouponResult(null); }}>
                      Uygula
                    </Button>
                  </>
                ) : (
                  <div style={{ color:'#b91c1c' }}>✕ {couponResult.msg}</div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
